import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { usePaymentInputs } from 'react-payment-inputs';
import styled from 'styled-components';

import { FIELD_KEYS, MONTH_OPTIONS } from '_constants/forms';
import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Spinner from 'components/atomic/Spinner';
import BaseField from 'components/atomic/fields/BaseField';
import SelectField from 'components/atomic/fields/SelectField';
import Text from 'components/atomic/layouts/Text';

const getYearOptions = () => {
  const startYear = new Date().getFullYear();

  return Array.from(new Array(20), (_, i) => {
    return { value: `${startYear + i}`, title: `${startYear + i}` };
  });
};

const checkIfCardExpired = ({ cardMonth, cardYear }) => {
  if (cardYear && cardMonth) {
    let expDate = new Date();
    expDate.setFullYear(cardYear, parseInt(cardMonth) - 1, 1);
    let today = new Date();

    if (expDate < today) {
      return true;
    } else {
      return false;
    }
  }
  return true;
};

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const AddPaymentMethodForm = ({ loading, error, submitDisabled, defaultValues, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    trigger,
    setValue,
    clearErrors,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const { meta, getCardNumberProps, getCVCProps } = usePaymentInputs();
  const { cardType, erroredInputs: erroredFields } = meta;

  const values = useWatch({ control });

  const [monthAutocompleteValue, setMonthAutocompleteValue] = useState('');
  const [yearAutocompleteValue, setYearAutocompleteValue] = useState('');

  const handleMonthAutocompleteValue = (e) => {
    const { value } = e.target || {};
    setMonthAutocompleteValue(value);
  };

  const handleYearAutocompleteValue = (e) => {
    const { value } = e.target || {};
    setYearAutocompleteValue(value);
  };

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (FIELD_KEYS.cvc in erroredFields && !erroredFields[FIELD_KEYS.cvc]) {
      trigger(FIELD_KEYS.cvc);
    }
  }, [erroredFields, trigger]);

  useEffect(() => {
    if (monthAutocompleteValue) {
      setMonthAutocompleteValue('');
      setValue(FIELD_KEYS.cardMonth, monthAutocompleteValue, {
        shouldValidate: true,
      });
    }
  }, [monthAutocompleteValue, setValue]);

  useEffect(() => {
    if (yearAutocompleteValue) {
      setYearAutocompleteValue('');
      setValue(FIELD_KEYS.cardYear, yearAutocompleteValue, {
        shouldValidate: true,
      });
    }
  }, [yearAutocompleteValue, setValue]);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12}>
          <BaseField
            {...getFieldProps(FIELD_KEYS.cardNumber, {
              validationOptions: { erroredFields },
            })}
            icon={<Icon alias={cardType?.type || ICON_ALIASES.testcard} />}
            iconSpace={50}
            getPaymentProps={getCardNumberProps}
            data-threeds="pan"
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>

        <select
          hidden
          id="cc-exp-month"
          name="cc-exp-month"
          autoComplete="cc-exp-month"
          value={monthAutocompleteValue}
          onChange={handleMonthAutocompleteValue}
        >
          <option value="" hidden />
          {MONTH_OPTIONS.map(({ title, value }) => (
            <option key={value} value={value}>
              {title}
            </option>
          ))}
        </select>

        <select
          hidden
          id="cc-exp-year"
          name="cc-exp-year"
          autoComplete="cc-exp-year"
          value={yearAutocompleteValue}
          onChange={handleYearAutocompleteValue}
        >
          <option value="" hidden />
          {getYearOptions().map(({ title, value }) => (
            <option key={value} value={value}>
              {title}
            </option>
          ))}
        </select>

        <Col xs={12}>
          <SelectField
            {...getFieldProps(FIELD_KEYS.cardMonth, {
              validationOptions: {
                values,
                clearErrors,
                checkIfCardExpired,
              },
            })}
            searchable={!monthAutocompleteValue}
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>

        <Col xs={12}>
          <SelectField
            {...getFieldProps(FIELD_KEYS.cardYear, {
              validationOptions: {
                values,
                clearErrors,
                checkIfCardExpired,
              },
            })}
            searchable={!yearAutocompleteValue}
            options={getYearOptions()}
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>

        <Col xs={12}>
          <BaseField
            {...getFieldProps(FIELD_KEYS.cvc, {
              validationOptions: { erroredFields },
            })}
            getPaymentProps={getCVCProps}
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>

        <Col xs={12}>
          <Button type="submit" disabled={submitDisabled} color={theme.colors.gray.primary}>
            Save Information
          </Button>
        </Col>
      </Row>

      {loading && (
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
          <StyledMessageContainer>
            <Spinner />
            <div>Updating...</div>
          </StyledMessageContainer>
        </Text>
      )}

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </Form>
  );
};

AddPaymentMethodForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default AddPaymentMethodForm;
